/**
 * Owner: Masa Bayu
 * File: qxp-parts.ts
 * Date:Time: 2026-09-14 20:33
 * Description: QXP Core reference parts for 28A — refs only, not QXW content.
 */
import { createQXPId } from './id';
import { assertOptionalTrimmed, assertOrder, assertValidId } from './guards';
import { QXP_ACCESSIBILITY_REF_KINDS, QXP_ASSET_REF_KINDS, QXP_CONTENT_REF_KINDS, QXP_DESIGN_REF_KINDS, QXP_DIAGNOSTIC_CODES, QXP_DIAGNOSTIC_MESSAGE_KEYS, QXP_DIAGNOSTIC_SEVERITIES, QXP_LAYOUT_REF_KINDS, QXP_NAVIGATION_REF_KINDS, QXP_STRUCTURE_REF_KINDS, } from './qxp-codes';
export const QXP_PACKAGE_ID_PREFIX = 'qxppkg';
export const QXP_REF_ID_PREFIX = 'qxpref';
export const QXP_DIAGNOSTIC_ID_PREFIX = 'qxpdiag';
function assertTargetRef(value, label) {
    if (typeof value !== 'string' || value.trim().length === 0) {
        throw new Error(`${label} must be a non-empty string`);
    }
    return value.trim();
}
export function createQXPPublicationIdentity(options = {}) {
    const publicationId = options.publicationId !== undefined ? options.publicationId : createQXPId('qxppub');
    assertValidId(publicationId, 'QXP publicationId');
    return Object.freeze({
        publicationId,
        title: assertOptionalTrimmed(options.title, 'QXP publication title'),
        sourceDocumentRef: assertOptionalTrimmed(options.sourceDocumentRef, 'QXP publication sourceDocumentRef'),
        editionRef: assertOptionalTrimmed(options.editionRef, 'QXP publication editionRef'),
        languageRef: assertOptionalTrimmed(options.languageRef, 'QXP publication languageRef'),
    });
}
export function createQXPContentRef(options) {
    if (!QXP_CONTENT_REF_KINDS.includes(options.kind)) {
        throw new Error('QXP content ref kind is unknown');
    }
    assertOrder(options.order, 'QXP content ref order');
    const refId = options.refId !== undefined ? options.refId : createQXPId(QXP_REF_ID_PREFIX);
    assertValidId(refId, 'QXP content refId');
    return Object.freeze({
        refId,
        kind: options.kind,
        targetRef: assertTargetRef(options.targetRef, 'QXP content targetRef'),
        order: options.order,
        label: assertOptionalTrimmed(options.label, 'QXP content label'),
    });
}
export function createQXPStructureRef(options) {
    if (!QXP_STRUCTURE_REF_KINDS.includes(options.kind)) {
        throw new Error('QXP structure ref kind is unknown');
    }
    assertOrder(options.order, 'QXP structure ref order');
    const refId = options.refId !== undefined ? options.refId : createQXPId(QXP_REF_ID_PREFIX);
    assertValidId(refId, 'QXP structure refId');
    const parentRefId = options.parentRefId === undefined || options.parentRefId === null ? null : options.parentRefId;
    if (parentRefId !== null) {
        assertValidId(parentRefId, 'QXP structure parentRefId');
    }
    return Object.freeze({
        refId,
        kind: options.kind,
        targetRef: assertTargetRef(options.targetRef, 'QXP structure targetRef'),
        order: options.order,
        parentRefId,
        label: assertOptionalTrimmed(options.label, 'QXP structure label'),
    });
}
export function createQXPLayoutRef(options) {
    if (!QXP_LAYOUT_REF_KINDS.includes(options.kind)) {
        throw new Error('QXP layout ref kind is unknown');
    }
    assertOrder(options.order, 'QXP layout ref order');
    const refId = options.refId !== undefined ? options.refId : createQXPId(QXP_REF_ID_PREFIX);
    assertValidId(refId, 'QXP layout refId');
    return Object.freeze({
        refId,
        kind: options.kind,
        targetRef: assertTargetRef(options.targetRef, 'QXP layout targetRef'),
        order: options.order,
        label: assertOptionalTrimmed(options.label, 'QXP layout label'),
    });
}
export function createQXPDesignRef(options) {
    if (!QXP_DESIGN_REF_KINDS.includes(options.kind)) {
        throw new Error('QXP design ref kind is unknown');
    }
    assertOrder(options.order, 'QXP design ref order');
    const refId = options.refId !== undefined ? options.refId : createQXPId(QXP_REF_ID_PREFIX);
    assertValidId(refId, 'QXP design refId');
    return Object.freeze({
        refId,
        kind: options.kind,
        targetRef: assertTargetRef(options.targetRef, 'QXP design targetRef'),
        order: options.order,
        label: assertOptionalTrimmed(options.label, 'QXP design label'),
    });
}
export function createQXPAssetRef(options) {
    if (!QXP_ASSET_REF_KINDS.includes(options.kind)) {
        throw new Error('QXP asset ref kind is unknown');
    }
    assertOrder(options.order, 'QXP asset ref order');
    const refId = options.refId !== undefined ? options.refId : createQXPId(QXP_REF_ID_PREFIX);
    assertValidId(refId, 'QXP asset refId');
    return Object.freeze({
        refId,
        kind: options.kind,
        targetRef: assertTargetRef(options.targetRef, 'QXP asset targetRef'),
        order: options.order,
        mediaType: assertOptionalTrimmed(options.mediaType, 'QXP asset mediaType'),
        label: assertOptionalTrimmed(options.label, 'QXP asset label'),
    });
}
export function createQXPNavigationRef(options) {
    if (!QXP_NAVIGATION_REF_KINDS.includes(options.kind)) {
        throw new Error('QXP navigation ref kind is unknown');
    }
    assertOrder(options.order, 'QXP navigation ref order');
    const refId = options.refId !== undefined ? options.refId : createQXPId(QXP_REF_ID_PREFIX);
    assertValidId(refId, 'QXP navigation refId');
    return Object.freeze({
        refId,
        kind: options.kind,
        targetRef: assertTargetRef(options.targetRef, 'QXP navigation targetRef'),
        order: options.order,
        label: assertOptionalTrimmed(options.label, 'QXP navigation label'),
    });
}
export function createQXPAccessibilityRef(options) {
    if (!QXP_ACCESSIBILITY_REF_KINDS.includes(options.kind)) {
        throw new Error('QXP accessibility ref kind is unknown');
    }
    assertOrder(options.order, 'QXP accessibility ref order');
    const refId = options.refId !== undefined ? options.refId : createQXPId(QXP_REF_ID_PREFIX);
    assertValidId(refId, 'QXP accessibility refId');
    return Object.freeze({
        refId,
        kind: options.kind,
        targetRef: assertTargetRef(options.targetRef, 'QXP accessibility targetRef'),
        order: options.order,
        label: assertOptionalTrimmed(options.label, 'QXP accessibility label'),
    });
}
export function createQXPMetadataRef(options) {
    if (typeof options.key !== 'string' || options.key.trim().length === 0) {
        throw new Error('QXP metadata key must be a non-empty string');
    }
    const entryId = options.entryId !== undefined ? options.entryId : createQXPId('qxpmeta');
    assertValidId(entryId, 'QXP metadata entryId');
    return Object.freeze({
        entryId,
        key: options.key.trim(),
        valueRef: assertOptionalTrimmed(options.valueRef, 'QXP metadata valueRef'),
    });
}
export function createQXPIntegrityMetadata(options = {}) {
    return Object.freeze({
        checksum: assertOptionalTrimmed(options.checksum, 'QXP integrity checksum'),
        compiledAt: assertOptionalTrimmed(options.compiledAt, 'QXP integrity compiledAt'),
        sourceRevision: assertOptionalTrimmed(options.sourceRevision, 'QXP integrity sourceRevision'),
        compilerRef: assertOptionalTrimmed(options.compilerRef, 'QXP integrity compilerRef'),
    });
}
export function createQXPDiagnostic(options) {
    if (!QXP_DIAGNOSTIC_SEVERITIES.includes(options.severity)) {
        throw new Error('QXP diagnostic severity must be one of: error, warning, info');
    }
    if (!QXP_DIAGNOSTIC_CODES.includes(options.code)) {
        throw new Error('QXP diagnostic code is unknown');
    }
    if (typeof options.path !== 'string' || options.path.trim().length === 0) {
        throw new Error('QXP diagnostic path must be a non-empty string');
    }
    const diagnosticId = options.diagnosticId !== undefined
        ? options.diagnosticId
        : createQXPId(QXP_DIAGNOSTIC_ID_PREFIX);
    assertValidId(diagnosticId, 'QXP diagnosticId');
    const messageKey = options.messageKey !== undefined
        ? options.messageKey.trim()
        : QXP_DIAGNOSTIC_MESSAGE_KEYS[options.code];
    if (messageKey.length === 0) {
        throw new Error('QXP diagnostic messageKey must be a non-empty string');
    }
    return Object.freeze({
        diagnosticId,
        severity: options.severity,
        code: options.code,
        path: options.path.trim(),
        messageKey,
    });
}
export function normalizeQXPDiagnostics(values) {
    if (values === undefined) {
        return Object.freeze([]);
    }
    const seen = new Set();
    const normalized = [];
    for (const [index, item] of values.entries()) {
        const diagnostic = createQXPDiagnostic(item);
        if (seen.has(diagnostic.diagnosticId)) {
            throw new Error(`QXP diagnosticId "${diagnostic.diagnosticId}" is duplicated at index ${String(index)}`);
        }
        seen.add(diagnostic.diagnosticId);
        normalized.push(diagnostic);
    }
    return Object.freeze(normalized);
}
//# sourceMappingURL=qxp-parts.js.map